import { trackFullStoryEvent } from "@expert/monitoring";
import { playNotificationSound } from "@/notification/playNotificationSound";

import type { ChatClosureInfo, ChatMessage } from "./types";

/**
 * Provider-agnostic chat callbacks used by the UI and app logic.
 * Providers must translate their native events into these calls.
 */
export interface ChatEventCallbacks {
    /** New or sent message (normalized). Play notifications and update UI */
    onMessageAdded: (message: ChatMessage) => Promise<void>;

    /** A chat session was created. extraData is provider-specific metadata */
    onChatAdded: (extraData?: Record<string, unknown>) => Promise<void>;

    /** A chat session was closed. closedBy and time are optional metadata */
    onChatClosed: (data: ChatClosureInfo) => Promise<void>;
}

export function useStatelessChatCallbacks(): ChatEventCallbacks {
    const onMessageAdded = async (message: ChatMessage) => {
        if (message.senderType === "customer") {
            await playNotificationSound("newMessage");
        }
    };

    const onChatAdded = async (extraData?: Record<string, unknown>) => {
        const eventProperties = extraData || {};

        trackFullStoryEvent("ChatAdded", eventProperties);
        await playNotificationSound("newChat");
    };

    const onChatClosed = async (data: ChatClosureInfo) => {
        const { contactId, reason } = data;
        trackFullStoryEvent("ChatClosed", { contactId, reason });
    };

    return { onMessageAdded, onChatAdded, onChatClosed };
}
